import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { X, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Images
const BG_MAIN = 'https://images.unsplash.com/photo-1762254928684-477525f48207?w=1920&q=80&auto=format';
const BG_AERIAL = 'https://images.pexels.com/photos/3048211/pexels-photo-3048211.jpeg?auto=compress&cs=tinysrgb&w=1920';

// Loading status messages
const STATUS_MESSAGES = [
  'Scansione ambiente in corso...',
  'Preparazione sedute interattive...',
  'Sincronizzazione layout piscina...',
  'Abilitazione prenotazioni...',
  'Caricamento esperienza immersiva...',
];

// Ghost hotspots
const HOTSPOTS = [
  { id: 1, x: 22, y: 45, label: 'Tavolo VIP', sublabel: 'Presto disponibile' },
  { id: 2, x: 55, y: 60, label: 'Pool Bed Premium', sublabel: 'Presto disponibile' },
  { id: 3, x: 78, y: 38, label: 'Zona Lounge', sublabel: 'Presto disponibile' },
  { id: 4, x: 35, y: 72, label: 'Lettino Piscina', sublabel: 'Presto disponibile' },
  { id: 5, x: 68, y: 52, label: 'Area Privata', sublabel: 'Presto disponibile' },
];

// Aerial hotspots
const AERIAL_HOTSPOTS = [
  { id: 1, x: 30, y: 40, label: 'Piscina Principale' },
  { id: 2, x: 60, y: 30, label: 'Area Tavoli' },
  { id: 3, x: 45, y: 65, label: 'Zona Lettini' },
  { id: 4, x: 75, y: 55, label: 'Bar & Lounge' },
];

function LoadingBar() {
  return (
    <div className="w-32 h-[2px] bg-white/10 rounded-full overflow-hidden">
      <motion.div
        className="h-full bg-gradient-to-r from-amber-300/60 to-white/40"
        animate={{ x: ['-100%', '200%'] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        style={{ width: '50%' }}
      />
    </div>
  );
}

function StatusCycler() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex(prev => (prev + 1) % STATUS_MESSAGES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <AnimatePresence mode="wait">
      <motion.p
        key={index}
        className="font-body text-[10px] tracking-[0.15em] uppercase text-white/40"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.4 }}
      >
        {STATUS_MESSAGES[index]}
      </motion.p>
    </AnimatePresence>
  );
}

function Hotspot({ spot, isAerial }) {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      className="absolute z-20 cursor-default"
      style={{ left: `${spot.x}%`, top: `${spot.y}%` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: spot.id * 0.3 + 1.5 }}
    >
      {/* Outer pulse */}
      <motion.div
        className="absolute -inset-3 rounded-full border border-white/10"
        animate={{ scale: [1, 1.8, 1], opacity: [0.3, 0, 0.3] }}
        transition={{ duration: 3, repeat: Infinity, delay: spot.id * 0.5 }}
      />
      {/* Core dot */}
      <motion.div
        className={`w-3 h-3 rounded-full backdrop-blur-sm transition-all duration-300 ${
          hovered
            ? 'bg-amber-300/60 shadow-[0_0_20px_rgba(212,175,55,0.4)]'
            : 'bg-white/15 border border-white/20'
        }`}
        animate={hovered ? {} : { opacity: [0.4, 0.8, 0.4] }}
        transition={{ duration: 2.5, repeat: Infinity }}
      />
      {/* Tooltip */}
      <AnimatePresence>
        {hovered && (
          <motion.div
            className="absolute left-1/2 -translate-x-1/2 bottom-6 whitespace-nowrap"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
          >
            <div className="bg-black/60 backdrop-blur-xl border border-white/10 px-4 py-2 rounded-sm">
              <p className="font-body text-[11px] tracking-[0.1em] uppercase text-amber-200/90">
                {spot.label}
              </p>
              {!isAerial && (
                <p className="font-body text-[9px] text-white/40 mt-0.5">
                  {spot.sublabel}
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function EarlyAccessModal({ onClose }) {
  const [form, setForm] = useState({ name: '', email: '', event_type: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.event_type) return;
    setLoading(true);
    try {
      await fetch(`${API_URL}/api/virtual-tour/early-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
    } catch {
      // Non-critical
    }
    setSubmitted(true);
    setLoading(false);
  };

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center px-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        className="relative w-full max-w-md bg-[#0a0a0a]/95 border border-white/10 backdrop-blur-xl p-8"
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/30 hover:text-white/60 transition-colors"
          data-testid="modal-close"
        >
          <X size={18} />
        </button>

        {submitted ? (
          <motion.div
            className="text-center py-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="w-12 h-12 rounded-full border border-amber-300/40 flex items-center justify-center mx-auto mb-6">
              <motion.div
                className="w-2 h-2 rounded-full bg-amber-300"
                animate={{ scale: [1, 1.4, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </div>
            <p className="font-heading text-xl text-ivory tracking-wide mb-2">
              Registrazione Completata
            </p>
            <p className="font-body text-xs text-white/40 leading-relaxed">
              Sarai tra i primi a esplorare La Villa.<br />
              Ti contatteremo presto.
            </p>
          </motion.div>
        ) : (
          <>
            <p className="font-body text-[10px] tracking-[0.2em] uppercase text-amber-300/60 mb-2">
              Accesso Anticipato
            </p>
            <p className="font-heading text-xl text-ivory tracking-wide mb-1">
              Esplora La Villa per primo
            </p>
            <p className="font-body text-xs text-white/30 mb-8 leading-relaxed">
              Registrati per ricevere l'accesso esclusivo al tour interattivo.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="text"
                placeholder="Nome"
                value={form.name}
                onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 px-4 py-3 text-sm text-ivory placeholder:text-white/20 font-body focus:outline-none focus:border-amber-300/30 transition-colors"
                data-testid="early-access-name"
              />
              <input
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 px-4 py-3 text-sm text-ivory placeholder:text-white/20 font-body focus:outline-none focus:border-amber-300/30 transition-colors"
                data-testid="early-access-email"
              />
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'pool_day', label: 'Pool Day' },
                  { value: 'private_event', label: 'Evento Privato' },
                  { value: 'night', label: 'Night Experience' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, event_type: opt.value }))}
                    className={`py-2.5 px-2 border text-[10px] tracking-[0.1em] uppercase font-body transition-all ${
                      form.event_type === opt.value
                        ? 'border-amber-300/40 text-amber-200 bg-amber-300/5'
                        : 'border-white/10 text-white/30 hover:border-white/20'
                    }`}
                    data-testid={`event-type-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <button
                type="submit"
                disabled={loading || !form.name || !form.email || !form.event_type}
                className="w-full py-3 border border-amber-300/30 text-amber-200/80 font-body text-xs tracking-[0.15em] uppercase hover:bg-amber-300/5 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="notify-me-btn"
              >
                {loading ? 'Invio...' : 'Avvisami'}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

export default function VirtualTour() {
  const [screen, setScreen] = useState('main'); // 'main' | 'aerial'
  const [showModal, setShowModal] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const containerRef = useRef(null);

  // Preload images
  useEffect(() => {
    let loaded = 0;
    const total = 2;
    const onLoad = () => {
      loaded++;
      if (loaded >= total) setImagesLoaded(true);
    };
    [BG_MAIN, BG_AERIAL].forEach(src => {
      const img = new Image();
      img.onload = onLoad;
      img.onerror = onLoad;
      img.src = src;
    });
  }, []);

  // Auto-transition to aerial view
  useEffect(() => {
    if (!imagesLoaded) return;
    const timer = setTimeout(() => setScreen('aerial'), 10000);
    return () => clearTimeout(timer);
  }, [imagesLoaded]);

  // Auto-return to main
  useEffect(() => {
    if (screen !== 'aerial') return;
    const timer = setTimeout(() => setScreen('main'), 8000);
    return () => clearTimeout(timer);
  }, [screen]);

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 bg-black overflow-hidden cursor-default select-none"
      data-testid="virtual-tour"
    >
      {/* ===== LOADING STATE ===== */}
      <AnimatePresence>
        {!imagesLoaded && (
          <motion.div
            className="absolute inset-0 z-50 bg-black flex flex-col items-center justify-center"
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
          >
            <motion.img
              src="/images/logo-new-white.png"
              alt="La Villa"
              className="h-8 mb-6"
              animate={{ opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <LoadingBar />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== MAIN SCENE ===== */}
      <AnimatePresence mode="wait">
        {screen === 'main' && imagesLoaded && (
          <motion.div
            key="main"
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 2 }}
          >
            {/* Background with drift */}
            <motion.div
              className="absolute inset-0"
              animate={{
                scale: [1.05, 1.12, 1.08, 1.05],
                x: ['0%', '1.5%', '-0.5%', '0%'],
                y: ['0%', '-1%', '0.5%', '0%'],
              }}
              transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
            >
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage: `url(${BG_MAIN})`,
                  filter: 'blur(6px) brightness(0.35) saturate(1.2)',
                  transform: 'scale(1.1)',
                }}
              />
            </motion.div>

            {/* Water reflection overlay */}
            <motion.div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'linear-gradient(180deg, transparent 40%, rgba(59,130,246,0.03) 70%, rgba(59,130,246,0.06) 100%)',
              }}
              animate={{ opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 4, repeat: Infinity }}
            />

            {/* Light shimmer */}
            <motion.div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'radial-gradient(ellipse at 60% 50%, rgba(212,175,55,0.04) 0%, transparent 60%)',
              }}
              animate={{
                opacity: [0.3, 0.8, 0.3],
                x: ['-5%', '5%', '-5%'],
              }}
              transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
            />

            {/* Vignette */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.6) 100%)',
              }}
            />

            {/* Ghost Hotspots */}
            {HOTSPOTS.map(spot => (
              <Hotspot key={spot.id} spot={spot} isAerial={false} />
            ))}

            {/* ===== CENTER TEXT ===== */}
            <div className="absolute inset-0 flex flex-col items-center justify-center z-30 px-6">
              <motion.div
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 1.2 }}
              >
                <motion.p
                  className="font-body text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-amber-300/50 mb-4"
                  animate={{ opacity: [0.4, 0.8, 0.4] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  Preview Tour 3D
                </motion.p>

                <h1 className="font-heading text-4xl sm:text-5xl md:text-7xl text-ivory tracking-[0.05em] mb-2 flex items-center justify-center gap-4 flex-wrap"
                  style={{ textShadow: '0 0 60px rgba(212,175,55,0.15), 0 0 120px rgba(212,175,55,0.05)' }}
                >
                  ENTRA A <img src="/images/logo-new-white.png" alt="La Villa" className="h-[2.2em] inline-block relative top-[7px]" />
                </h1>

                <motion.p
                  className="font-heading text-lg sm:text-xl md:text-2xl italic text-amber-300/50 tracking-[0.15em] mb-8"
                  animate={{ opacity: [0.5, 0.9, 0.5] }}
                  transition={{ duration: 4, repeat: Infinity }}
                >
                  COMING SOON
                </motion.p>

                <div className="space-y-1.5 mb-12">
                  {['Esplora lo spazio.', 'Scegli il tuo tavolo.', 'Prenota la tua esperienza.'].map((line, i) => (
                    <motion.p
                      key={line}
                      className="font-body text-xs sm:text-sm text-white/30 tracking-wide"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 1 + i * 0.3 }}
                    >
                      {line}
                    </motion.p>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* ===== TOP-RIGHT STATUS ===== */}
            <motion.div
              className="absolute top-6 right-6 z-30 text-right"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2 }}
            >
              <p className="font-body text-[10px] tracking-[0.15em] uppercase text-white/25 mb-2">
                Tour Interattivo in Preparazione
              </p>
              <LoadingBar />
              <div className="mt-2">
                <StatusCycler />
              </div>
            </motion.div>

            {/* ===== BACK BUTTON ===== */}
            <motion.div
              className="absolute top-6 left-6 z-30"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
            >
              <Link
                to="/"
                className="flex items-center gap-2 text-white/25 hover:text-white/50 transition-colors font-body text-[10px] tracking-[0.15em] uppercase"
                data-testid="back-home"
              >
                <ArrowLeft size={14} />
                La Villa
              </Link>
            </motion.div>
          </motion.div>
        )}

        {/* ===== AERIAL SCREEN ===== */}
        {screen === 'aerial' && imagesLoaded && (
          <motion.div
            key="aerial"
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 2 }}
          >
            {/* Background */}
            <motion.div
              className="absolute inset-0"
              animate={{
                scale: [1.02, 1.08, 1.02],
                y: ['0%', '-1%', '0%'],
              }}
              transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
            >
              <div
                className="absolute inset-0 bg-cover bg-center"
                style={{
                  backgroundImage: `url(${BG_AERIAL})`,
                  filter: 'blur(4px) brightness(0.3) saturate(1.1)',
                  transform: 'scale(1.1)',
                }}
              />
            </motion.div>

            {/* Vignette */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.7) 100%)',
              }}
            />

            {/* Aerial Hotspots */}
            {AERIAL_HOTSPOTS.map(spot => (
              <Hotspot key={spot.id} spot={spot} isAerial={true} />
            ))}

            {/* Center overlay */}
            <div className="absolute inset-0 flex flex-col items-center justify-center z-30 px-6">
              <motion.div
                className="text-center"
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5, duration: 1 }}
              >
                <p className="font-body text-[10px] tracking-[0.3em] uppercase text-amber-300/40 mb-4">
                  Anteprima Mappa
                </p>
                <h2 className="font-heading text-3xl md:text-5xl text-ivory tracking-wide mb-3"
                  style={{ textShadow: '0 0 40px rgba(212,175,55,0.1)' }}
                >
                  Seleziona il Tuo Posto
                </h2>
                <motion.p
                  className="font-heading text-base md:text-lg italic text-amber-300/40 tracking-[0.1em]"
                  animate={{ opacity: [0.3, 0.7, 0.3] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  Presto Disponibile
                </motion.p>
              </motion.div>
            </div>

            {/* Back to main */}
            <motion.div
              className="absolute top-6 left-6 z-30"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
            >
              <Link
                to="/"
                className="flex items-center gap-2 text-white/25 hover:text-white/50 transition-colors font-body text-[10px] tracking-[0.15em] uppercase"
              >
                <ArrowLeft size={14} />
                La Villa
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== BOTTOM CTA ===== */}
      {imagesLoaded && (
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-40 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.5, duration: 1 }}
        >
          <button
            onClick={() => setShowModal(true)}
            className="group relative px-8 py-3 border border-white/15 hover:border-amber-300/30 transition-all duration-500"
            data-testid="early-access-btn"
          >
            <span className="font-body text-[11px] tracking-[0.2em] uppercase text-white/50 group-hover:text-amber-200/80 transition-colors">
              Accesso Anticipato
            </span>
            <motion.div
              className="absolute inset-0 border border-amber-300/0 group-hover:border-amber-300/10"
              animate={{ scale: [1, 1.05, 1], opacity: [0, 0.3, 0] }}
              transition={{ duration: 3, repeat: Infinity }}
            />
          </button>
        </motion.div>
      )}

      {/* ===== FINAL OVERLAY TEXT ===== */}
      {imagesLoaded && (
        <motion.div
          className="absolute bottom-20 left-1/2 -translate-x-1/2 z-30 text-center hidden md:block"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 4, duration: 2 }}
        >
          <p className="font-body text-[9px] tracking-[0.2em] uppercase text-white/15 leading-relaxed">
            Cammina nello spazio &middot; Scegli il tuo momento &middot; Prenota il tuo posto
          </p>
        </motion.div>
      )}

      {/* ===== MODAL ===== */}
      <AnimatePresence>
        {showModal && <EarlyAccessModal onClose={() => setShowModal(false)} />}
      </AnimatePresence>
    </div>
  );
}
